// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import {
  ActionIcon,
  Avatar,
  Box,
  Checkbox,
  Group,
  MultiSelect,
  Select,
  SelectItemProps,
  Space,
  Stack,
  Text as MantineText,
} from '@mantine/core';
import { i18n } from '@lingui/core';
import { FC, forwardRef, useContext, useEffect } from 'react';
import { IconBallpen, IconUser } from '@tabler/icons';
import { DateRangePickerWithSelect } from '../../Atoms/inputs/DateRangePickerWithSelect';
import { AgendaGroupMultiSelectItem } from '../../Atoms/MultiSelect/AgendaGroupMultiSelect';
import AddTaskIcon from '../../Organismns/Tasks/AddTaskIcon';
import { TaskListContext } from '../../Service/Context/TaskListContext';
import { WindowContext } from '../../Service/Context/WindowContext';
import { useBookitupScope, useBookitupSubscription } from '../../Service/Context/BookitupSubscriptionContext';
import { useAssignableApplicationUsers } from '../../Service/hooks/useAssignableApplicationUsers';
import { useDateRangeItem } from '../../Service/hooks/useDateRangeItem';
import { ProfileContext } from '../../Service/Context/ProfileContext';
import { getAgendaGroupDisplayEntityType } from '../../Types/Enums/AgendaGroupEntityType';
import { addDays, lastDateBeforeNextMidnight, lastMidnight } from '../../Utils/dateUtils';
import BookingStateItem from '../EventList/BookingStateItem';
import { ThemeText, ThemeTitle } from '../../Atoms/ThemeText';
import { openTasksGroupsModal } from './TasksModals';

interface ApplicationUserFilterItemProps extends SelectItemProps {
  email: string;
  firstName?: string;
  lastName?: string;
}

type ApplicationUserLabelData = {
  email: string;
  firstName?: string;
  lastName?: string;
};

const getApplicationUserInitials = ({ firstName, lastName, email }: ApplicationUserLabelData) => {
  const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
  return initials || email.charAt(0).toUpperCase();
};

const ApplicationUserFilterItem = forwardRef<HTMLDivElement, ApplicationUserFilterItemProps>(
  ({ label, email, firstName, lastName, ...rest }: ApplicationUserFilterItemProps, ref) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div ref={ref} {...rest}>
      <Group noWrap spacing="sm">
        <Avatar size={30} radius="xl">
          {getApplicationUserInitials({ firstName, lastName, email })}
        </Avatar>
        <Stack spacing={0}>
          <MantineText size="sm" weight={500}>
            {label}
          </MantineText>
          {email && label !== email && (
            <MantineText size="xs" color="dimmed">
              {email}
            </MantineText>
          )}
        </Stack>
      </Group>
    </div>
  ),
);

const TasksNavFilter: FC<{ closeDrawer?: () => unknown }> = ({ closeDrawer }) => {
  const { taskListFilter, taskListContent, changeFilter, loadGroups } = useContext(TaskListContext);
  const { isScopeSelected: hasGeneralTasksScope } = useBookitupScope('GENERAL_TASKS');
  const { teamAddonPresent } = useBookitupSubscription();
  const { selectedTaskGroups, selectedDateRange, includeCompleted, applicationUsersOfInterests } = taskListFilter;
  const { taskGroups } = taskListContent;
  const { bookitupTeam } = useContext(ProfileContext);
  const loadingApplicationUsers = bookitupTeam.loading && !bookitupTeam.sharesLoaded;
  const applicationUserItems = useAssignableApplicationUsers(bookitupTeam);
  const dateRangePickerMinimum = new Date(2020, 1, 1);
  const dateRangePickerMaximum = new Date(new Date().getFullYear() + 10, 1, 1);
  const startOfDay = lastMidnight();
  const { sm } = useContext(WindowContext);

  const onDateChange = (values: [Date | null, Date | null]) => {
    if (values[0] && values[1]) {
      changeFilter({ selectedDateRange: [values[0], values[1]] });
    }
  };

  useEffect(() => {
    if (!hasGeneralTasksScope || !teamAddonPresent) {
      return;
    }

    bookitupTeam.ensureSharesLoaded().catch(() => undefined);
  }, [bookitupTeam, hasGeneralTasksScope, teamAddonPresent]);

  const displayItems = taskGroups.map((g) => ({
    value: g.id,
    label: `${g.name} (${getAgendaGroupDisplayEntityType(g.entityType)})`,
    checked: selectedTaskGroups.indexOf(g) !== -1,
  }));

  const onIncludeCompletedChange = (checked: boolean) => {
    changeFilter({ includeCompleted: checked });
  };

  const getGroupInputText = () =>
    // eslint-disable-next-line no-nested-ternary
    selectedTaskGroups.length === taskGroups.length
      ? 'Alle Gruppen'
      : selectedTaskGroups.length > 0
      ? `(${selectedTaskGroups.length}) Gruppen`
      : 'Keine Gruppen';

  const onChange = (value: string) => {
    const item = taskGroups.find((g) => g.id === value);

    if (!item) {
      return;
    }

    const mutableGroups = [...selectedTaskGroups];

    const idx = mutableGroups.indexOf(item);

    if (idx !== -1) {
      mutableGroups.splice(idx, 1);
    } else {
      mutableGroups.push(item);
    }

    changeFilter({ selectedTaskGroups: mutableGroups });
  };

  const onApplicationUsersChange = (values: string[]) => {
    changeFilter({ applicationUsersOfInterests: values.map(Number), page: 0 });
  };

  return (
    <>
      {!sm && (
        <Group p="sm" position="apart" sx={{ alignItems: 'center' }}>
          <ThemeTitle order={3}> {i18n._('common.tasks')}</ThemeTitle>
          <AddTaskIcon />
        </Group>
      )}
      <Space h="md" />
      <ThemeText pl={10}>Datumsbereich</ThemeText>
      <Box p="xs">
        <DateRangePickerWithSelect
          zIndex={100}
          shadow="xl"
          labelSeparator="bis"
          placeholder="Pick dates range"
          value={[selectedDateRange[0], selectedDateRange[1]]}
          onChange={onDateChange}
          clearable={false}
          variant="filled"
          showRanges
        />
      </Box>
      <BookingStateItem
        label="Maximum"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, dateRangePickerMaximum] })}
        active={useDateRangeItem(dateRangePickerMinimum, dateRangePickerMaximum, selectedDateRange)}
      />
      <BookingStateItem
        label="Heute"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, lastDateBeforeNextMidnight()] })}
        active={useDateRangeItem(dateRangePickerMinimum, lastDateBeforeNextMidnight(), selectedDateRange)}
      />
      <BookingStateItem
        label="Morgen"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, addDays(1, startOfDay)] })}
        active={useDateRangeItem(dateRangePickerMinimum, addDays(1, startOfDay), selectedDateRange)}
      />
      <BookingStateItem
        label="In einer Woche"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, addDays(7, startOfDay)] })}
        active={useDateRangeItem(dateRangePickerMinimum, addDays(7, startOfDay), selectedDateRange)}
      />
      <BookingStateItem
        label="In zwei Woche"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, addDays(14, startOfDay)] })}
        active={useDateRangeItem(dateRangePickerMinimum, addDays(14, startOfDay), selectedDateRange)}
      />
      <BookingStateItem
        label="In einem Monat"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, addDays(30, startOfDay)] })}
        active={useDateRangeItem(dateRangePickerMinimum, addDays(30, startOfDay), selectedDateRange)}
      />
      <BookingStateItem
        label="In einem Jahr"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, addDays(365, startOfDay)] })}
        active={useDateRangeItem(dateRangePickerMinimum, addDays(365, startOfDay), selectedDateRange)}
      />

      <Space h="md" />

      <Group position="apart" mt="xl">
        <ThemeText pl={10}>Filtergruppe</ThemeText>
        <ActionIcon style={{ marginRight: 10 }}>
          <IconBallpen
            size={16}
            onClick={() => {
              if (closeDrawer) closeDrawer();
              openTasksGroupsModal(loadGroups);
            }}
          />
        </ActionIcon>
      </Group>

      <Box p="xs">
        <Select
          data={displayItems}
          variant="filled"
          placeholder={getGroupInputText()}
          value={null}
          clearButtonLabel="Übersichtliche Auswahl"
          onChange={onChange}
          zIndex={100}
          shadow="xl"
          itemComponent={AgendaGroupMultiSelectItem}
        />
      </Box>
      {hasGeneralTasksScope && teamAddonPresent && (
        <>
          <Space h="sm" />
          <ThemeText pl={10}>Zugewiesen an</ThemeText>
          <Box p="xs">
            <MultiSelect
              searchable
              clearable
              data={applicationUserItems}
              value={applicationUsersOfInterests.map((applicationUserId) => applicationUserId.toString())}
              onChange={onApplicationUsersChange}
              icon={<IconUser size={16} />}
              variant="filled"
              placeholder="Alle Personen"
              nothingFound="Keine Personen gefunden"
              disabled={loadingApplicationUsers}
              itemComponent={ApplicationUserFilterItem}
              zIndex={100}
              shadow="xl"
            />
          </Box>
        </>
      )}
      <Space h="sm" />
      <Checkbox
        ml={15}
        label="fertige Aufgaben"
        checked={includeCompleted}
        onChange={(event) => onIncludeCompletedChange(event.currentTarget.checked)}
      />
    </>
  );
};

export default TasksNavFilter;
